import { MessageSquareText, Trash2, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  deleteRecentQuestionChat,
  removeRecentQuestion,
  restoreRecentQuestion,
  useRecentQuestions,
  type RecentQuestion,
  type RemovedRecentQuestion,
} from "../../watchlist/recentQuestions";
import BottomToastPortal from "../BottomToastPortal";
import { useAlertLabels } from "./labels";

type RecentQuestionsProps = {
  onOpen: (question: RecentQuestion) => void;
  /** Shown in place of the list; defaults to the chat history's own wording. */
  emptyLabel?: string;
};

/** The resumable conversations, as a bare list: each caller names the section it sits in. */
const RecentQuestions = ({ onOpen, emptyLabel }: RecentQuestionsProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "watchlist" });
  const { t: tRoot } = useTranslation();
  const { relativeTime } = useAlertLabels();
  const questions = useRecentQuestions();
  const [removed, setRemoved] = useState<RemovedRecentQuestion | null>(null);
  const removedRef = useRef<RemovedRecentQuestion | null>(null);
  const deleteTimer = useRef<number | null>(null);

  const commitRemoval = useCallback(() => {
    if (deleteTimer.current !== null) window.clearTimeout(deleteTimer.current);
    deleteTimer.current = null;
    if (removedRef.current) deleteRecentQuestionChat(removedRef.current.question.id);
    removedRef.current = null;
    setRemoved(null);
  }, []);

  useEffect(
    () => () => {
      if (deleteTimer.current !== null) window.clearTimeout(deleteTimer.current);
      if (removedRef.current) deleteRecentQuestionChat(removedRef.current.question.id);
    },
    [],
  );

  const handleDelete = (question: RecentQuestion) => {
    commitRemoval();
    const nextRemoved = removeRecentQuestion(question.id);
    if (!nextRemoved) return;
    removedRef.current = nextRemoved;
    setRemoved(nextRemoved);
    deleteTimer.current = window.setTimeout(commitRemoval, 5000);
  };

  const handleUndo = () => {
    if (deleteTimer.current !== null) window.clearTimeout(deleteTimer.current);
    deleteTimer.current = null;
    if (removedRef.current) restoreRecentQuestion(removedRef.current);
    removedRef.current = null;
    setRemoved(null);
  };

  if (questions.length === 0 && !removed)
    return <p className="text-sm text-text-dim">{emptyLabel ?? tRoot("chat_history_empty")}</p>;

  return (
    <>
      {/* A quiet list rather than a bordered card: these are things you might
          return to, not a table to study. No frame, no dividers, muted text —
          the row only picks up a surface on hover. */}
      {questions.length > 0 && (
        <div className="-mx-2 flex flex-col">
          {questions.map(question => (
            <div
              key={question.id}
              className="group flex w-full items-center rounded-lg transition hover:bg-muted-surface"
            >
              <button
                type="button"
                onClick={() => onOpen(question)}
                className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
              >
                <MessageSquareText className="h-4 w-4 shrink-0 text-text-dim" aria-hidden="true" />
                {/* One line per question: the title takes what it needs and
                    the scope trails it as a quiet aside, both truncating
                    together rather than stacking the row two-high. */}
                <span className="flex min-w-0 flex-1 items-baseline gap-2 truncate">
                  <span className="truncate text-sm text-text-mid transition group-hover:text-text">
                    {question.title}
                  </span>
                  <span className="truncate text-xs text-text-dim">{question.scopeLabel}</span>
                </span>
                <span className="shrink-0 text-xs text-text-dim">
                  {relativeTime(question.updatedAt)}
                </span>
              </button>
              {/* Hidden until the row is hovered or the button is keyboard
                  focused, so the list stays free of standing controls. */}
              <button
                type="button"
                onClick={() => handleDelete(question)}
                title={t("delete_chat")}
                aria-label={t("delete_chat")}
                className="mr-2 rounded-md p-2 text-text-dim opacity-0 transition hover:bg-red-500/10 hover:text-red-600 focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-red-500/40 group-hover:opacity-100"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
      {removed && (
        <BottomToastPortal>
          <div
            role="status"
            className="pointer-events-auto flex items-center gap-4 rounded-lg border border-outline bg-surface-strong/95 px-4 py-3 text-sm text-text shadow-xl backdrop-blur-md"
          >
            <span>{t("chat_deleted")}</span>
            <button
              type="button"
              onClick={handleUndo}
              className="font-semibold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              {t("undo")}
            </button>
            <button
              type="button"
              onClick={commitRemoval}
              title={t("dismiss_deleted_chat")}
              aria-label={t("dismiss_deleted_chat")}
              className="-mr-1 rounded-md p-1 text-text-dim transition hover:bg-muted-surface hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </BottomToastPortal>
      )}
    </>
  );
};

export default RecentQuestions;
